import React, { useEffect, useState } from "react";
import { ShieldAlert, ArrowLeftRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { toast } from "sonner";

export function CoachAdminInvites() {
  const [oversight, setOversight] = useState(null);
  const [invites, setInvites] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ role: "campus_administrator", name: "", email: "" });
  const loadOv = React.useCallback(() => api.get("/coach/oversight").then(({ data }) => setOversight(data)).catch(() => {}), []);
  const loadInv = React.useCallback(() => api.get("/coach/invites").then(({ data }) => setInvites(data.items || [])).catch(() => {}), []);
  useEffect(() => { loadOv(); loadInv(); }, [loadOv, loadInv]);

  const noAdmin = oversight && oversight.district_admins.length === 0 && oversight.campus_admins.length === 0;
  const send = async (role, name, email) => {
    setBusy(true);
    try {
      await api.post("/coach/invite-admin", { role, name: name || null, email });
      toast.success(`Invite sent to ${email}`);
      setOpen(false); setForm({ role: "campus_administrator", name: "", email: "" });
      loadInv(); loadOv();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send invite"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="coach-invites-section">
      {noAdmin && (
        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="coach-no-admin-banner">
          <ShieldAlert className="h-5 w-5 text-amber-600" />
          <span className="text-sm font-semibold text-slate-800">Your team has no District or Campus Administrator.</span>
          <Button size="sm" className="ml-auto rounded-full bg-slate-900 hover:bg-slate-800" onClick={() => setOpen(true)} data-testid="coach-invite-admin-btn">Invite an administrator</Button>
        </div>
      )}
      <div className="mb-3 flex items-center gap-2">
        <p className="text-sm font-bold uppercase tracking-wide text-slate-500">Administrator invites</p>
        <Button size="sm" variant="outline" className="ml-auto rounded-full" onClick={() => setOpen(true)} data-testid="coach-invite-admin-btn2">Invite an administrator</Button>
      </div>
      <p className="mb-4 text-sm text-slate-500">Invite a District or Campus Administrator to oversee your team. They get instant-access oversight once they accept.</p>
        {invites.filter((iv) => iv.status !== "accepted").length === 0 ? (
          <p className="text-sm text-slate-400" data-testid="coach-invites-empty">No pending administrator invites.</p>
        ) : (
          <div className="space-y-2">
            {invites.filter((iv) => iv.status !== "accepted").map((iv) => (
              <div key={iv.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-100 bg-slate-50 p-3" data-testid={`coach-invite-${iv.id}`}>
                <div>
                  <p className="text-sm font-semibold text-slate-800">{iv.name || iv.email} <span className="font-normal text-slate-400">· {iv.role_label}</span></p>
                  <p className="text-xs text-slate-500">{iv.email}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${iv.status === "expired" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`} data-testid={`coach-invite-status-${iv.id}`}>{iv.status}</span>
                  {iv.status === "pending" && <Button size="sm" variant="outline" className="rounded-full" disabled={busy} onClick={() => send(iv.role, iv.name, iv.email)} data-testid={`coach-invite-resend-${iv.id}`}>Resend</Button>}
                </div>
              </div>
            ))}
          </div>
        )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="coach-invite-dialog">
          <DialogHeader><DialogTitle className="font-heading">Invite an administrator</DialogTitle><DialogDescription>Send an invite link so they can create their oversight account.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
                <SelectTrigger data-testid="coach-invite-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="campus_administrator">Campus Administrator</SelectItem>
                  <SelectItem value="district_administrator">District Administrator</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Name (optional)</Label><Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="coach-invite-name" /></div>
            <div className="space-y-2"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="coach-invite-email" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="rounded-full bg-slate-900 hover:bg-slate-800" disabled={busy || !form.email} onClick={() => send(form.role, form.name, form.email)} data-testid="coach-invite-send">Send invite</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const ROLE_REQ_LABEL = { coach: "Coach", district_administrator: "District Administrator", campus_administrator: "Campus Administrator" };
const ROLE_REQ_STATUS = { pending: "bg-amber-100 text-amber-700", approved: "bg-emerald-100 text-emerald-700", rejected: "bg-red-100 text-red-700" };

export function CoachRoleRequests() {
  const [items, setItems] = useState([]);
  const [oversight, setOversight] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ action: "add", role: "campus_administrator", name: "", email: "", target: "" });
  const load = React.useCallback(() => api.get("/role-requests").then(({ data }) => setItems(data.items || [])).catch(() => {}), []);
  useEffect(() => { load(); api.get("/coach/oversight").then(({ data }) => setOversight(data)).catch(() => {}); }, [load]);

  const targets = oversight ? [
    ...oversight.district_admins.map((a) => ({ ...a, role: "district_administrator" })),
    ...oversight.campus_admins.map((a) => ({ ...a, role: "campus_administrator" })),
  ].filter((a) => a.id) : [];

  const canSubmit = form.action === "add" ? !!form.email : !!form.target;
  const submit = async () => {
    setBusy(true);
    try {
      let body;
      if (form.action === "add") {
        body = { action: "add", role: form.role, name: form.name || null, email: form.email };
      } else {
        const t = targets.find((x) => x.id === form.target);
        if (!t) { toast.error("Select who to deactivate"); setBusy(false); return; }
        body = { action: "deactivate", role: t.role, target_user_id: t.id, name: t.name, email: t.email };
      }
      await api.post("/role-requests", body);
      toast.success("Request submitted to System Administrator");
      setOpen(false); setForm({ action: "add", role: "campus_administrator", name: "", email: "", target: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not submit request"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="coach-role-request-section">
      <div className="mb-3 flex items-center gap-2">
        <ArrowLeftRight className="h-4 w-4 text-violet-600" />
        <p className="text-sm font-bold uppercase tracking-wide text-slate-500">Role change requests</p>
        <Button size="sm" className="ml-auto rounded-full bg-slate-900 hover:bg-slate-800" onClick={() => setOpen(true)} data-testid="coach-role-request-btn">Request a role change</Button>
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-slate-400" data-testid="coach-role-request-empty">No role change requests yet.</p>
      ) : (
        <div className="space-y-2">
          {items.map((r) => (
            <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-100 bg-slate-50 p-3" data-testid={`coach-role-request-${r.id}`}>
              <div>
                <p className="text-sm font-semibold text-slate-800"><span className="capitalize">{r.action}</span> {ROLE_REQ_LABEL[r.role] || r.role} <span className="font-normal text-slate-400">— {r.target_name || r.target_email || "existing account"}</span></p>
                <p className="text-xs text-slate-500">By {r.requested_by_name} · {new Date(r.created_at).toLocaleDateString("en-US", { timeZone: "America/Chicago" })}</p>
                {r.decline_reason && <p className="mt-0.5 text-xs text-red-600" data-testid={`coach-role-request-reason-${r.id}`}>Reason: “{r.decline_reason}”</p>}
              </div>
              <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${ROLE_REQ_STATUS[r.status] || "bg-slate-100 text-slate-600"}`} data-testid={`coach-role-request-status-${r.id}`}>{r.status}</span>
            </div>
          ))}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="coach-role-request-dialog" className="max-h-[90dvh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">Request a role change</DialogTitle><DialogDescription>Submitted to the System Administrator for approval.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label>Action</Label>
              <Select value={form.action} onValueChange={(v) => setForm((f) => ({ ...f, action: v }))}>
                <SelectTrigger data-testid="coach-role-request-action"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="add">Add a role</SelectItem>
                  <SelectItem value="deactivate">Deactivate a role</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.action === "add" ? (
              <>
                <div className="space-y-2">
                  <Label>Role</Label>
                  <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
                    <SelectTrigger data-testid="coach-role-request-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="campus_administrator">Campus Administrator</SelectItem>
                      <SelectItem value="district_administrator">District Administrator</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Name (optional)</Label><Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="coach-role-request-name" /></div>
                <div className="space-y-2"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="coach-role-request-email" /></div>
                <p className="text-xs text-slate-500">On approval, an invite is emailed so they get instant-access oversight.</p>
              </>
            ) : (
              <div className="space-y-2">
                <Label>Who to deactivate</Label>
                {targets.length === 0 ? (
                  <p className="text-sm text-slate-400" data-testid="coach-role-request-no-targets">No active administrators to deactivate.</p>
                ) : (
                  <Select value={form.target} onValueChange={(v) => setForm((f) => ({ ...f, target: v }))}>
                    <SelectTrigger data-testid="coach-role-request-target"><SelectValue placeholder="Select an administrator" /></SelectTrigger>
                    <SelectContent>
                      {targets.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} · {ROLE_REQ_LABEL[t.role]}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="rounded-full bg-slate-900 hover:bg-slate-800" disabled={busy || !canSubmit} onClick={submit} data-testid="coach-role-request-submit">Submit request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
